import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import SymbolPriceChart from '@/components/ui/SymbolPriceChart';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import EditEarningsRecommendationModal from '@/components/admin/recommendations/EditEarningsRecommendationModal';
import {
  faSpinner,
  faArrowLeft,
  faEdit,
  faChartPie,
  faChartLine,
  faCalendarAlt,
  faCheck,
  faTimes,
  faBell,
  faGlobe,
} from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, formatCurrencyCentValue, formatCurrency } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { PAGINATION } from '@/config';
import { ADMIN_TRADING_ROUTES } from '@/constants/routes';
import type { RecommendationEarnings, EarningsRecommendationID, SymbolHistoryEntry } from '@/models';
import type { GraphPoint } from '@/modelsAPI';

const pageSize = 50;

// Helper to format seconds as time HH:MM
function formatSecondsAsTime(seconds: number | null): string {
  if (seconds === null) return '-';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Helper to get state display info
function getStateInfo(state: string): { label: string; colorClass: string } {
  switch (state) {
    case 'OPEN':
      return { label: 'Offen', colorClass: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' };
    case 'FINISHED_ASSIGNED':
      return { label: 'Eingebucht', colorClass: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' };
    case 'FINISHED_UNASSIGNED':
      return { label: 'Erfolgreich beendet', colorClass: 'bg-lime-100 text-lime-800 dark:bg-lime-900/50 dark:text-lime-200' };
    default:
      return { label: state, colorClass: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' };
  }
}

export default function AdminEarningsRecommendationFullDetail() {
  const { isAdmin } = useUser();
  const { recommendationId } = useParams<{ recommendationId: string }>();
  const [recommendation, setRecommendation] = useState<RecommendationEarnings | null>(null);
  const [graphPoints, setGraphPoints] = useState<GraphPoint[]>([]);
  const [history, setHistory] = useState<SymbolHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [graphLoading, setGraphLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (recommendationId) {
      fetchRecommendationDetails();
    }
  }, [recommendationId]);

  const fetchChartGraph = async (rec: RecommendationEarnings) => {
    if (!rec.symbol?.id) return;

    try {
      setGraphLoading(true);
      // Convert RFCDate to ISO string for the API
      const start = new Date(rec.startDate).toISOString();
      const end = new Date(rec.endDate).toISOString();

      const chartResponse = await APIRoutes.getSymbolGraph(rec.symbol.id, {
        start,
        end,
        points: PAGINATION.CHART.DETAIL_POINTS
      });
      setGraphPoints(chartResponse.points);
    } catch (err) {
      console.error('Error loading chart graph:', err);
      handleAPIError(err);
    } finally {
      setGraphLoading(false);
    }
  };

  const fetchSymbolHistory = async (rec: RecommendationEarnings, page: number = 1) => {
    if (!rec.symbol?.id) return;

    try {
      setHistoryLoading(true);
      const cursorToken = page > 1 ? `$${page}` : undefined;

      // Convert RFCDate to ISO string for filtering
      const timestampAfter = new Date(rec.startDate).toISOString();
      const timestampBefore = new Date(rec.endDate).toISOString();

      const historyResponse = await APIRoutes.listSymbolHistory(rec.symbol.id, {
        limit: pageSize,
        next: cursorToken,
        timestampAfter,
        timestampBefore,
        sort: 'timestamp',
        sortDirection: 'ASC'
      });

      setHistory(historyResponse.history);
      setHistoryTotal(historyResponse.total);
      setCurrentPage(page);
    } catch (err) {
      console.error('Error loading symbol history:', err);
      handleAPIError(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchRecommendationDetails = async () => {
    if (!recommendationId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const recResponse = await APIRoutes.getEarningsRecommendation(recommendationId as EarningsRecommendationID);
      setRecommendation(recResponse);

      // Fetch chart and history in parallel
      await Promise.all([
        fetchChartGraph(recResponse),
        fetchSymbolHistory(recResponse)
      ]);
    } catch (err) {
      console.error('Critical error loading recommendation details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Empfehlungs-Details');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRecommendationUpdated = (updatedRecommendation: RecommendationEarnings) => {
    setRecommendation(updatedRecommendation);
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Earnings-Empfehlung nicht verfügbar"
        message={error ?? 'Die Empfehlungs-Details konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchRecommendationDetails}
        onBack={() => window.history.back()}
      />
    );
  }

  if (loading || recommendation === null) {
    return (
      <div className="flex justify-center items-center py-12">
        <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
      </div>
    );
  }

  const stateInfo = getStateInfo(recommendation.state);
  const currency = recommendation.symbol?.currentData?.currency ?? 'USD';

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to={ADMIN_TRADING_ROUTES.EARNINGS_LIST}
            className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 dark:bg-green-900/50 p-2 rounded-lg">
              <FontAwesomeIcon icon={faChartPie} className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Admin Ansicht: {recommendation.symbolTicker}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Earnings-Empfehlung ID: {recommendation.id}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsEditModalOpen(true)}
            disabled={!isAdmin}
            className={`px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 transition-colors ${!isAdmin ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700 cursor-pointer'}`}
          >
            <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
            <span>Bearbeiten</span>
          </button>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Recommendation Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faChartPie} className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
            Empfehlungs-Daten
          </h2>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{recommendation.id}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Symbol Ticker</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.symbolTicker}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Strike-Preis</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.strikePrice, currency)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Prämie / Kontrakt</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.premium, currency)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Startdatum</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(recommendation.startDate)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Enddatum</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(recommendation.endDate)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stateInfo.colorClass}`}>
                    {stateInfo.label}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Win/Loss Delta</dt>
                <dd className={`mt-1 text-sm font-semibold ${recommendation.winLossDelta !== null && recommendation.winLossDelta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {recommendation.winLossDelta !== null ? `${recommendation.winLossDelta >= 0 ? '+' : ''}${recommendation.winLossDelta.toFixed(2)}%` : '-'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Aktueller Preis</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatCurrencyCentValue(recommendation.currentPrice, currency)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Entry-Preis</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.entryPrice !== null ? formatCurrencyCentValue(recommendation.entryPrice, currency) : '-'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Exit-Preis</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.exitPrice !== null ? formatCurrencyCentValue(recommendation.exitPrice, currency) : '-'}</dd>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Flags</h3>
              <div className="flex flex-wrap gap-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.boost ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Boost: {recommendation.boost ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.free ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Free: {recommendation.free ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.onlyCommunityMember ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Nur Community: {recommendation.onlyCommunityMember ? 'Ja' : 'Nein'}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${recommendation.excludeCommunityMember ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                  Community ausschließen: {recommendation.excludeCommunityMember ? 'Ja' : 'Nein'}
                </span>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Timestamps</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Erstellt</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.created)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Aktualisiert</dt>
                  <dd className="text-gray-900 dark:text-white">{recommendation.updated !== null ? formatDate(recommendation.updated) : '-'}</dd>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Flags Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faBell} className="w-4 h-4 mr-2 text-yellow-600 dark:text-yellow-400" />
            Benachrichtigungs-Status
          </h2>

          <div className="space-y-3">
            <NotificationFlag label="Discord Benachrichtigung" sent={recommendation.discordNotificationSent} />
            <NotificationFlag label="SCN Benachrichtigung" sent={recommendation.scnNotificationSent} />
            <NotificationFlag label="Push Benachrichtigung" sent={recommendation.pushNotificationSent} />
            <NotificationFlag label="Telegram Benachrichtigung" sent={recommendation.telegramNotificationSent} />
            <NotificationFlag label="Mail an Free-User" sent={recommendation.freeUserMailNotificationSent} />
            <NotificationFlag label="Mail an alle User" sent={recommendation.allUsersMailNotificationSent} />
            <NotificationFlag label="Discord Kommentar" sent={recommendation.discordNotificationCommentSent} />
            <NotificationFlag label="Telegram Kommentar" sent={recommendation.telegramNotificationCommentSent} />
            <NotificationFlag label="Mail Kommentar" sent={recommendation.mailNotificationCommentSent} />
            <NotificationFlag label="SCN Kommentar" sent={recommendation.scnNotificationCommentSent} />
            <NotificationFlag label="Push Kommentar" sent={recommendation.pushNotificationCommentSent} />
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 mt-4 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kommentar</h3>
            {recommendation.notificationComment !== '' ? (
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap break-words">
                {recommendation.notificationComment}
              </p>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500 italic">Kein Kommentar</p>
            )}
          </div>
        </div>
      </div>

      {/* Symbol & Exchange Data Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Symbol Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faChartLine} className="w-4 h-4 mr-2 text-cyan-600 dark:text-cyan-400" />
            Symbol-Daten
          </h2>

          {recommendation.symbol ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Ticker</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.symbol.ticker}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.name ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Branche</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.sector ?? '-'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Im Ticker anzeigen</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.symbol.showInTicker ? 'Ja' : 'Nein'}</dd>
                </div>
              </div>

              {recommendation.symbol.currentData && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Aktuelle Marktdaten</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <StockDataRow label="Kurs" value={formatCurrency(recommendation.symbol.currentData.price, currency)} />
                      <StockDataRow label="Eröffnungskurs" value={formatCurrency(recommendation.symbol.currentData.priceOpen, currency)} />
                      <StockDataRow label="Tageshoch" value={formatCurrency(recommendation.symbol.currentData.high, currency)} />
                      <StockDataRow label="Tagestief" value={formatCurrency(recommendation.symbol.currentData.low, currency)} />
                      <StockDataRow label="52W Hoch" value={formatCurrency(recommendation.symbol.currentData.high52, currency)} />
                      <StockDataRow label="52W Tief" value={formatCurrency(recommendation.symbol.currentData.low52, currency)} />
                      <StockDataRow label="Änderung" value={recommendation.symbol.currentData.change !== null ? `${recommendation.symbol.currentData.change >= 0 ? '+' : ''}${recommendation.symbol.currentData.change.toFixed(2)}` : '-'} />
                      <StockDataRow label="Änderung %" value={recommendation.symbol.currentData.changePct !== null ? `${recommendation.symbol.currentData.changePct.toFixed(2)}%` : '-'} />
                      <StockDataRow label="Vortagesschluss" value={formatCurrency(recommendation.symbol.currentData.closeYest, currency)} />
                      <StockDataRow label="Volumen" value={recommendation.symbol.currentData.volume?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Ø Volumen" value={recommendation.symbol.currentData.volumeAvg?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Marktkapitalisierung" value={formatCurrency(recommendation.symbol.currentData.marketCap, currency)} />
                      <StockDataRow label="KGV (P/E)" value={recommendation.symbol.currentData.pe?.toFixed(2) ?? '-'} />
                      <StockDataRow label="EPS" value={formatCurrency(recommendation.symbol.currentData.eps, currency)} />
                      <StockDataRow label="Beta" value={recommendation.symbol.currentData.beta?.toFixed(2) ?? '-'} />
                      <StockDataRow label="Aktien im Umlauf" value={recommendation.symbol.currentData.shares?.toLocaleString('de-DE') ?? '-'} />
                      <StockDataRow label="Währung" value={currency} />
                      <StockDataRow label="Datenverzögerung" value={recommendation.symbol.currentData.dataDelay !== null ? `${recommendation.symbol.currentData.dataDelay} Min.` : '-'} />
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Keine Symbol-Daten verfügbar</p>
          )}
        </div>

        {/* Exchange Data Card */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <FontAwesomeIcon icon={faGlobe} className="w-4 h-4 mr-2 text-green-600 dark:text-green-400" />
            Börsen-Daten
          </h2>

          {recommendation.stockExchange ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ID</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{recommendation.stockExchange.id}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Kennung</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-semibold">{recommendation.stockExchange.identifier}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.stockExchange.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Zeitzone</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{recommendation.stockExchange.timezone}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Öffnungszeit</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatSecondsAsTime(recommendation.stockExchange.openingTime)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Schließzeit</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatSecondsAsTime(recommendation.stockExchange.closingTime)}</dd>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Erstellt</dt>
                    <dd className="text-gray-900 dark:text-white">{formatDate(recommendation.stockExchange.created)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500 dark:text-gray-400">Aktualisiert</dt>
                    <dd className="text-gray-900 dark:text-white">{recommendation.stockExchange.updated !== null ? formatDate(recommendation.stockExchange.updated) : '-'}</dd>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-sm">Keine Börsen-Daten verfügbar</p>
          )}
        </div>
      </div>

      {/* Price Chart Section */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-8">
        <div className="flex items-center mb-6">
          <FontAwesomeIcon icon={faChartLine} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Kursverlauf ({formatDate(recommendation.startDate)} - {formatDate(recommendation.endDate)})
          </h2>
          {graphLoading && (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin ml-2" />
          )}
        </div>
        <SymbolPriceChart
          points={graphPoints}
          currency={currency}
          height={400}
          strikePrice={recommendation.strikePrice / 100}
        />
      </div>

      {/* Symbol History Table Section */}
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
        <div className="flex items-center p-6 border-b border-gray-200 dark:border-gray-700">
          <FontAwesomeIcon icon={faCalendarAlt} className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Preis-Historie ({historyTotal})
          </h2>
          {historyLoading && (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin ml-2" />
          )}
        </div>

        {history.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faCalendarAlt} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Keine Historie verfügbar
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Für den Zeitraum dieser Empfehlung sind keine historischen Daten vorhanden.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Aufgezeichnet am
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Kurs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Änderung
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Volumen
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Marktkapitalisierung
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {history.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDate(entry.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.price, entry.data.currency ?? 'USD')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm ${(entry.data.change ?? 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {entry.data.change !== null && entry.data.changePct !== null ? `${entry.data.change >= 0 ? '+' : ''}${entry.data.change.toFixed(2)} (${entry.data.changePct.toFixed(2)}%)` : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {entry.data.volume?.toLocaleString('de-DE') ?? '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatCurrency(entry.data.marketCap, entry.data.currency ?? 'USD')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <PageBasedPagination
              currentPage={currentPage}
              totalCount={historyTotal}
              pageSize={pageSize}
              onPageChange={(page) => fetchSymbolHistory(recommendation, page)}
              loading={historyLoading}
            />
          </>
        )}
      </div>

      {/* Edit Modal */}
      <EditEarningsRecommendationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        recommendation={recommendation}
        onRecommendationUpdated={handleRecommendationUpdated}
      />
    </>
  );
}

// Helper component for notification flag display
function NotificationFlag({ label, sent }: { label: string; sent: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
      <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
      <span className={`inline-flex items-center ${sent ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
        <FontAwesomeIcon icon={sent ? faCheck : faTimes} className="w-4 h-4" />
        <span className="ml-1 text-xs font-medium">{sent ? 'Gesendet' : 'Nicht gesendet'}</span>
      </span>
    </div>
  );
}

// Helper component for stock data rows
function StockDataRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="text-gray-900 dark:text-white">{value}</dd>
    </div>
  );
}
